import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  type MarcaListItem,
  type MarcaRequest,
  type ModeloListItem,
  type ModeloRequest
} from '../types/marca';
import { MarcaService } from '../services/marcaService';

interface MarcaStore {
  // Marcas state
  marcas: MarcaListItem[];
  marcasLoading: boolean;
  marcasError: string | null;

  // Modelos state
  modelos: ModeloListItem[];
  modelosLoading: boolean;
  modelosError: string | null;

  // Selection state
  selectedMarca: MarcaListItem | null;

  // Marcas actions
  fetchMarcas: () => Promise<void>;
  saveMarca: (marca: MarcaRequest) => Promise<void>;
  clearMarcasError: () => void;

  // Modelos actions
  fetchModelos: () => Promise<void>;
  fetchModelosByMarca: (idMarca: number) => Promise<void>;
  saveModelo: (modelo: ModeloRequest) => Promise<void>;
  clearModelosError: () => void;

  // Selection actions
  setSelectedMarca: (marca: MarcaListItem | null) => void;
}

export const useMarcaStore = create<MarcaStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      marcas: [],
      marcasLoading: false,
      marcasError: null,

      modelos: [],
      modelosLoading: false,
      modelosError: null,

      selectedMarca: null,

      // Marcas actions
      fetchMarcas: async () => {
        set({ marcasLoading: true, marcasError: null });
        try {
          const marcas = await MarcaService.getMarcas();
          set({ marcas, marcasLoading: false });
        } catch (error) {
          set({
            marcasError: error instanceof Error ? error.message : 'Error fetching marcas',
            marcasLoading: false,
          });
        }
      },

      saveMarca: async (marca: MarcaRequest) => {
        set({ marcasLoading: true, marcasError: null });
        try {
          await MarcaService.saveMarca(marca);
          // Refresh the list after saving
          await get().fetchMarcas();
        } catch (error) {
          set({
            marcasError: error instanceof Error ? error.message : 'Error saving marca',
            marcasLoading: false,
          });
          throw error;
        }
      },

      clearMarcasError: () => set({ marcasError: null }),

      // Modelos actions
      fetchModelos: async () => {
        set({ modelosLoading: true, modelosError: null });
        try {
          const modelos = await MarcaService.getModelos();
          set({ modelos, modelosLoading: false });
        } catch (error) {
          set({
            modelosError: error instanceof Error ? error.message : 'Error fetching modelos',
            modelosLoading: false,
          });
        }
      },

      fetchModelosByMarca: async (idMarca: number) => {
        set({ modelosLoading: true, modelosError: null });
        try {
          const modelos = await MarcaService.getModelosByMarca(idMarca);
          set({ modelos, modelosLoading: false });
        } catch (error) {
          set({
            modelosError: error instanceof Error ? error.message : 'Error fetching modelos',
            modelosLoading: false,
          });
        }
      },

      saveModelo: async (modelo: ModeloRequest) => {
        set({ modelosLoading: true, modelosError: null });
        try {
          await MarcaService.saveModelo(modelo);
          // Refresh the list after saving
          if (modelo.idMarca) {
            await get().fetchModelosByMarca(modelo.idMarca);
          }
        } catch (error) {
          set({
            modelosError: error instanceof Error ? error.message : 'Error saving modelo',
            modelosLoading: false,
          });
          throw error;
        }
      },

      clearModelosError: () => set({ modelosError: null }),

      // Selection actions
      setSelectedMarca: (marca: MarcaListItem | null) => {
        set({ selectedMarca: marca, modelos: [] });
        if (marca) {
          get().fetchModelosByMarca(marca.idMarca);
        }
      },
    }),
    { name: 'marca-store' }
  )
);